import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Badge, Button, Card, CardBody, Input } from '@ludus/ui'
import type { AuditsApiError } from '../api/index.ts'
import { useAsk } from '../hooks/useChat.ts'
import { useBrain, useChat } from '../hooks/index.ts'
import type { ChatMessage } from '../types.ts'
import { timecode } from '../CardView/Transcript/TranscriptPanel.tsx'

/**
 * The audit chat (§9.6, §10.3, D-24): the thread with evidence as links to
 * card pages, polled only while a question has no answer, and «brain
 * online» from the heartbeat. Offline is a count of queued questions, not
 * an error — the session is simply away.
 */
export function ChatView({ auditId }: { auditId: string }) {
  const { t } = useTranslation()
  const [params] = useSearchParams()
  const { messages, waiting, error } = useChat(auditId)
  const { brain } = useBrain(auditId, waiting)
  const { trigger, isMutating } = useAsk(auditId)
  const [draft, setDraft] = useState(params.get('ask') ?? '')
  const [failure, setFailure] = useState<string | null>(null)

  useEffect(() => {
    const ask = params.get('ask')
    if (ask) setDraft(ask)
  }, [params])

  const queued = messages.filter((m) => m.role === 'user' && m.answeredAt === null).length

  const send = async (e: React.FormEvent) => {
    e.preventDefault()
    if (draft.trim() === '' || isMutating) return
    setFailure(null)
    try {
      await trigger({ text: draft.trim() })
      setDraft('')
    } catch (err) {
      setFailure((err as AuditsApiError).message)
    }
  }

  return (
    <div className="flex w-full max-w-4xl flex-1 flex-col gap-4 p-4 sm:p-6">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 pb-4">
        <div className="flex flex-col gap-1">
          <nav className="text-xs text-muted">
            <Link to={`/audits/${auditId}`} className="hover:underline">
              {t('pages.audits.card.backToAudit', { defaultValue: 'Audit' })}
            </Link>
          </nav>
          <h1 className="text-xl font-semibold text-foreground">
            {t('pages.audits.chat.title', { defaultValue: 'Audit chat' })}
          </h1>
        </div>
        <div className="flex items-center gap-2 text-xs" role="status">
          {brain?.online ? (
            <Badge tone="success" size="sm">
              {t('pages.audits.chat.online', { defaultValue: 'brain online' })}
            </Badge>
          ) : (
            <Badge tone="muted" size="sm">
              {t('pages.audits.chat.offline', {
                defaultValue: 'brain away · {{count}} queued',
                count: queued,
              })}
            </Badge>
          )}
        </div>
      </header>

      {error && (
        <p role="alert" className="text-xs text-toast-error-text">
          {error.message}
        </p>
      )}

      <ol className="flex flex-col gap-2">
        {messages.length === 0 && (
          <li className="text-xs text-muted">
            {t('pages.audits.chat.empty', {
              defaultValue: 'No questions yet. Ask about a card, a doubt, a number.',
            })}
          </li>
        )}
        {messages.map((message) => (
          <Message key={message.id} auditId={auditId} message={message} />
        ))}
        {waiting && (
          <li className="text-xs text-muted">
            {t('pages.audits.chat.waiting', { defaultValue: 'Waiting for the brain to poll…' })}
          </li>
        )}
      </ol>

      <form onSubmit={send} className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={t('pages.audits.chat.placeholder', { defaultValue: 'Ask the audit…' })}
          aria-label={t('pages.audits.chat.placeholder', { defaultValue: 'Ask the audit…' })}
        />
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={isMutating || draft.trim() === ''}
        >
          {t('pages.audits.chat.send', { defaultValue: 'Ask' })}
        </Button>
      </form>
      {failure && (
        <p role="alert" className="text-xs text-toast-error-text">
          {failure}
        </p>
      )}
    </div>
  )
}

function Message({ auditId, message }: { auditId: string; message: ChatMessage }) {
  const { t } = useTranslation()
  return (
    <li>
      <Card className={message.role === 'user' ? 'ml-8' : 'mr-8'}>
        <CardBody className="flex flex-col gap-1 text-sm">
          <span className="text-[11px] uppercase tracking-wide text-muted">
            {t(`pages.audits.chat.role.${message.role}`, { defaultValue: message.role })}
          </span>
          <p className="whitespace-pre-wrap">{message.text}</p>
          {message.evidence.length > 0 && (
            <ul className="flex flex-wrap gap-1">
              {message.evidence.map((item, i) => (
                <li key={i}>
                  <Link
                    className="text-xs text-primary hover:underline"
                    to={`/audits/${auditId}/cards/${item.ref_id}`}
                  >
                    {t(`pages.audits.chat.evidence.${item.kind}`, { defaultValue: item.kind })} ·{' '}
                    {item.ref_id}
                    {item.start_ms !== undefined && item.end_ms !== undefined
                      ? ` · ${timecode(item.start_ms)}–${timecode(item.end_ms)}`
                      : ''}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </li>
  )
}
